import type { AvaApiRESTBaseQueryArgs } from '@src/providers/UserContextProvider/avaApiRESTBaseQuery';
import type { ILPSettlementBatchResponse } from '@talos/kyoko';
import { v4 as uuid } from 'uuid';
import { baseRestApi, sendToastOnQueryFulfilled } from '../../../utils/rtkQuery';
import type { LPSettlementReportBlotterRow } from './UnsettledTransactions/blotter/lpSettlementReportRow';
import type {
  LPSettlementBatchMessageData,
  LPSettlementBatchRequest,
  LPSettlementInstructionRequest,
  SettlementBatchRequestEvent,
} from './generated/lpSettlementTypes';
import { toSettleBatchRequestEvent } from './utils/toSettleBatchRequestEvent';

const lpSettlementApiEndpoints = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    previewSettlementBatch: builder.query<
      ILPSettlementBatchResponse[],
      { selectedTrades: LPSettlementReportBlotterRow[]; homeCurrency: string }
    >({
      query: ({ selectedTrades, homeCurrency }) => {
        const body = prepareBatchRequest(selectedTrades, homeCurrency);
        return {
          method: 'POST',
          url: `/lp-settlement/preview`,
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        } satisfies AvaApiRESTBaseQueryArgs;
      },
      onQueryStarted: sendToastOnQueryFulfilled({
        errorMessage: 'Failed to generate settlement preview',
      }),
    }),
    createManualBatch: builder.mutation<
      ILPSettlementBatchResponse[],
      { selectedTrades: LPSettlementReportBlotterRow[]; homeCurrency: string }
    >({
      query: ({ selectedTrades, homeCurrency }) => {
        const body = prepareBatchRequest(selectedTrades, homeCurrency);
        return {
          method: 'POST',
          url: `/lp-settlement/batch`,
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        } satisfies AvaApiRESTBaseQueryArgs;
      },
      onQueryStarted: sendToastOnQueryFulfilled({
        successMessage: () => `Manual batch created`,
        errorMessage: 'Failed to create manual batch',
      }),
    }),
    markComplete: builder.mutation<
      ILPSettlementBatchResponse[],
      { settleInstructionID: string; txHash: string; comments: string }
    >({
      query: ({ settleInstructionID, txHash, comments }) => {
        const body: LPSettlementInstructionRequest = {
          ClientReqID: uuid(),
          SettleInstructionID: settleInstructionID,
          TxHash: txHash,
          Comments: comments,
        };
        return {
          method: 'POST',
          url: `/lp-settlement/complete`,
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        } satisfies AvaApiRESTBaseQueryArgs;
      },
      onQueryStarted: sendToastOnQueryFulfilled({
        successMessage: () => `Batch instruction marked as complete`,
        errorMessage: 'Failed to mark Batch instruction as complete',
      }),
    }),
    transfer: builder.mutation<
      ILPSettlementBatchResponse[],
      {
        settleInstructionID: string;
        transferFromMarketAccount?: string;
        transferToMarketAccount?: string;
        transferCurrency?: string;
        transferAmount?: string;
        comments: string;
      }
    >({
      query: ({
        settleInstructionID,
        transferFromMarketAccount,
        transferToMarketAccount,
        transferCurrency,
        transferAmount,
        comments,
      }) => {
        const body: LPSettlementInstructionRequest = {
          ClientReqID: uuid(),
          SettleInstructionID: settleInstructionID,
          TransferFromMarketAccount: transferFromMarketAccount,
          TransferToMarketAccount: transferToMarketAccount,
          TransferCurrency: transferCurrency,
          TransferAmount: transferAmount,
          Comments: comments,
        };
        return {
          method: 'POST',
          url: `/lp-settlement/transfer`,
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        } satisfies AvaApiRESTBaseQueryArgs;
      },
      onQueryStarted: sendToastOnQueryFulfilled({
        successMessage: () => `Transfer initiated`,
        errorMessage: 'Failed to initiate Transfer',
      }),
    }),
    ungroupBatch: builder.mutation<
      ILPSettlementBatchResponse[],
      {
        settlementBatch: LPSettlementBatchMessageData;
      }
    >({
      query: ({ settlementBatch }) => {
        return {
          method: 'DELETE',
          url: `/lp-settlement/batch/${settlementBatch.BatchID}?Revision=${settlementBatch.Revision}`,
        } satisfies AvaApiRESTBaseQueryArgs;
      },
      onQueryStarted: sendToastOnQueryFulfilled({
        successMessage: () => `Unbatch successful`,
        errorMessage: 'Failed to unbatch - please try again.',
      }),
    }),
    removeFromBatch: builder.mutation<
      ILPSettlementBatchResponse[],
      {
        batchID: string;
        batchRevision: number;
        events: SettlementBatchRequestEvent[];
      }
    >({
      query: ({ batchID, batchRevision, events }) => {
        const body: LPSettlementBatchRequest = {
          ClientReqID: uuid(),
          SettleBatchRevision: batchRevision,
          SettleBatchRequestEvents: events,
        };
        return {
          method: 'PUT',
          url: `/lp-settlement/batch/${batchID}/remove`,
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        } satisfies AvaApiRESTBaseQueryArgs;
      },
    }),
    addToBatch: builder.mutation<
      ILPSettlementBatchResponse[],
      {
        batchID: string;
        batchRevision: number;
        events: SettlementBatchRequestEvent[];
      }
    >({
      query: ({ batchID, batchRevision, events }) => {
        const body: LPSettlementBatchRequest = {
          ClientReqID: uuid(),
          SettleBatchRevision: batchRevision,
          SettleBatchRequestEvents: events,
        };
        return {
          method: 'PUT',
          url: `/lp-settlement/batch/${batchID}/add`,
          headers: {
            'Content-Type': 'application/json',
          },
          body,
        } satisfies AvaApiRESTBaseQueryArgs;
      },
      onQueryStarted: sendToastOnQueryFulfilled({
        errorMessage: 'Failed to add transactions to batch',
      }),
    }),
  }),
});

export const {
  usePreviewSettlementBatchQuery,
  useCreateManualBatchMutation,
  useMarkCompleteMutation,
  useTransferMutation,
  useUngroupBatchMutation,
  useRemoveFromBatchMutation,
  useAddToBatchMutation,
} = lpSettlementApiEndpoints;

function prepareBatchRequest(
  selectedTrades: LPSettlementReportBlotterRow[],
  homeCurrency: string
): LPSettlementBatchRequest {
  return {
    ClientReqID: uuid(),
    EquivalentCurrency: homeCurrency,
    SettleBatchRequestEvents: selectedTrades.map(toSettleBatchRequestEvent),
  };
}
